import {
  Component,
  EventEmitter,
  Input,
  Output,
  inject,
} from '@angular/core'
import { CommonModule } from '@angular/common'
import { FormsModule } from '@angular/forms'
import { DialogModule } from 'primeng/dialog'
import { ButtonModule } from 'primeng/button'
import { InputTextModule } from 'primeng/inputtext'
import { DropdownModule } from 'primeng/dropdown'
import { TooltipModule } from 'primeng/tooltip'
import { finalize } from 'rxjs'
import {
  InterventionsService,
  AffairOptimizationResult,
  AffairOptimizationSlotData,
  SchedulePoolAffairResponse,
} from '../../../../../interventions/services/interventions.service'
import { UserService } from '../../../../../users/services/user.service'
import {
  AgenciesService,
  AgencySelectItem,
} from '../../../../services/agencies.service'
import { formatFullDate } from '../../pool-optimization-formatters'
import { environment } from '../../../../../../../environments/environment'

/**
 * Finalisation d'une affaire vivier manuelle (draft) : choix du créneau,
 * agence (filtrée par la source ERP du technicien), puis création
 * affaire + intervention dans EROS en un clic.
 */
@Component({
  selector: 'app-pool-affair-schedule-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DialogModule,
    ButtonModule,
    InputTextModule,
    DropdownModule,
    TooltipModule,
  ],
  templateUrl: './pool-affair-schedule-dialog.component.html',
  styleUrl: './pool-affair-schedule-dialog.component.scss',
})
export class PoolAffairScheduleDialogComponent {
  private interventionsService = inject(InterventionsService)
  private userService = inject(UserService)
  private agenciesService = inject(AgenciesService)

  @Input() set context(
    value: {
      affair: AffairOptimizationResult
      proposals: AffairOptimizationSlotData[]
    } | null
  ) {
    this._context = value
    if (value) this.onOpen()
  }
  get context() {
    return this._context
  }
  private _context: {
    affair: AffairOptimizationResult
    proposals: AffairOptimizationSlotData[]
  } | null = null

  @Output() closed = new EventEmitter<void>()
  @Output() scheduled = new EventEmitter<SchedulePoolAffairResponse>()

  selectedProposal: AffairOptimizationSlotData | null = null
  startTime = ''
  /** Plage horaire annoncée au client (isTimeSlot EROS). */
  isTimeSlot = false
  startTimeSlot = ''
  endTimeSlot = ''
  isConfirmed = false
  showCustomerDetails = false
  customerFirstname = ''
  customerPhone = ''
  customerEmail = ''

  agencies: AgencySelectItem[] = []
  selectedAgencyId: number | null = null
  agenciesLoading = false
  erpSource: string | null = null
  /** Agence cliente de l'affaire (affaires existantes) : présélectionnée si présente dans la liste. */
  private prefillAgencyId: number | null = null

  /** Cache session : source ERP par technicien (évite un GET user par clic). */
  private technicianSourceCache = new Map<number, string>()

  submitting = false
  submitError: string | null = null
  /** Échec partiel : l'affaire EROS existe (mlRef), l'intervention a échoué. */
  partialFailure: SchedulePoolAffairResponse | null = null

  readonly formatFullDate = formatFullDate

  get visible(): boolean {
    return this._context !== null
  }

  private onOpen(): void {
    this.submitError = null
    this.partialFailure = null
    this.submitting = false
    this.isConfirmed = false
    this.isTimeSlot = false
    this.startTimeSlot = ''
    this.endTimeSlot = ''
    this.showCustomerDetails = false
    this.customerFirstname = ''
    this.customerPhone = ''
    this.customerEmail = ''
    this.agencies = []
    this.selectedAgencyId = null
    this.erpSource = null
    this.prefillAgencyId = this._context?.affair?.agencyId ?? null

    const first = this._context?.proposals?.[0] ?? null
    this.selectProposal(first)
  }

  close(): void {
    this._context = null
    this.closed.emit()
  }

  selectProposal(proposal: AffairOptimizationSlotData | null): void {
    this.selectedProposal = proposal
    this.startTime = this.extractUtcTime(proposal?.arrivalTime) ?? '08:00'

    // Plage horaire : déduite automatiquement de l'heure d'arrivée du créneau
    // (matin / après-midi), comme le wizard affair-schedule-steps.
    if (this.isTimeSlot || !this.startTimeSlot) {
      this.applyAutoTimeSlot(this.startTime)
    }

    if (proposal?.technicianId) {
      this.loadAgenciesForTechnician(proposal.technicianId)
    }
  }

  /**
   * L'heure d'intervention a changé : réaligner automatiquement la plage
   * horaire (matin / après-midi) sur la nouvelle heure. L'utilisateur peut
   * ensuite l'ajuster manuellement dans les champs « Entre » / « Et ».
   */
  onStartTimeChange(value: string): void {
    this.startTime = value
    if (/^\d{2}:\d{2}$/.test(value)) {
      this.applyAutoTimeSlot(value)
    }
  }

  /** Activation de la plage horaire : la déduire immédiatement de l'heure. */
  onTimeSlotToggle(value: boolean): void {
    this.isTimeSlot = value
    if (value) {
      this.applyAutoTimeSlot(this.startTime)
    }
  }

  /**
   * Déduit et applique la plage horaire (matin 08:00-12:00 / après-midi
   * 13:00-18:00) à partir d'une heure « HH:mm », selon la même règle que
   * l'étape Programmation du wizard (affair-schedule-steps) :
   *  - 13h ≤ heure < 18h → après-midi ;
   *  - sinon → matin (valeur par défaut).
   */
  private applyAutoTimeSlot(time: string): void {
    const hour = parseInt((time || '').split(':')[0], 10)
    if (!isNaN(hour) && hour >= 13 && hour < 18) {
      this.startTimeSlot = '13:00'
      this.endTimeSlot = '18:00'
    } else {
      this.startTimeSlot = '08:00'
      this.endTimeSlot = '12:00'
    }
  }

  /**
   * Extrait « HH:mm » d'un horaire de créneau. Le backend encode l'heure
   * métier dans le timestamp UTC (convention vivier) : on lit donc les
   * composantes UTC de l'ISO. Gère aussi les formats « HH:mm(:ss) » simples.
   */
  private extractUtcTime(value: string | null | undefined): string | null {
    if (!value) return null

    if (/^\d{2}:\d{2}/.test(value)) {
      return value.substring(0, 5)
    }

    const d = new Date(value)
    if (Number.isNaN(d.getTime())) return null

    return (
      String(d.getUTCHours()).padStart(2, '0') +
      ':' +
      String(d.getUTCMinutes()).padStart(2, '0')
    )
  }

  /**
   * Résout la source ERP du technicien du créneau (mono-source) puis charge
   * les agences existant dans cet ERP. Source technicien et liste d'agences
   * sont mises en cache : changer de créneau au sein de la même source ERP
   * ne recharge rien.
   */
  private loadAgenciesForTechnician(technicianId: number): void {
    const cachedSource = this.technicianSourceCache.get(technicianId)
    if (cachedSource) {
      this.applyErpSource(cachedSource)
      return
    }

    this.agenciesLoading = true

    this.userService.getUser(technicianId).subscribe({
      next: (user) => {
        const erpSource = (user?.erpSource ?? '').toLowerCase()
        if (erpSource !== 'abm' && erpSource !== 'nrj') {
          this.agenciesLoading = false
          this.submitError =
            'Impossible de déterminer la source ERP du technicien sélectionné.'
          return
        }
        this.technicianSourceCache.set(technicianId, erpSource)
        this.applyErpSource(erpSource)
      },
      error: () => {
        this.agenciesLoading = false
        this.submitError = 'Impossible de charger le technicien du créneau.'
      },
    })
  }

  /** Charge les agences de la source si elle change (réponse cachée côté service). */
  private applyErpSource(erpSource: string): void {
    if (this.erpSource === erpSource && this.agencies.length > 0) {
      this.agenciesLoading = false
      return
    }

    this.erpSource = erpSource
    this.agenciesLoading = true

    this.agenciesService
      .getForSelect(erpSource as 'abm' | 'nrj')
      .pipe(finalize(() => (this.agenciesLoading = false)))
      .subscribe({
        next: (agencies) => {
          this.agencies = agencies ?? []
          // Ne pas réinitialiser un choix déjà fait si l'agence reste valide
          if (
            this.selectedAgencyId &&
            !this.agencies.some((a) => a.id === this.selectedAgencyId)
          ) {
            this.selectedAgencyId = null
          }
          // Affaire existante : présélectionner l'agence cliente de l'affaire
          // si elle existe dans l'ERP du technicien (liste chargée).
          if (
            !this.selectedAgencyId &&
            this.prefillAgencyId &&
            this.agencies.some((a) => a.id === this.prefillAgencyId)
          ) {
            this.selectedAgencyId = this.prefillAgencyId
          }
        },
        error: () => {
          this.submitError = 'Impossible de charger les agences.'
        },
      })
  }

  get canSubmit(): boolean {
    const timeSlotValid =
      !this.isTimeSlot ||
      (/^\d{2}:\d{2}$/.test(this.startTimeSlot) &&
        /^\d{2}:\d{2}$/.test(this.endTimeSlot) &&
        this.startTimeSlot < this.endTimeSlot)

    return (
      !this.submitting &&
      !!this.selectedProposal?.technicianId &&
      !!this.selectedAgencyId &&
      /^\d{2}:\d{2}$/.test(this.startTime) &&
      timeSlotValid
    )
  }

  submit(): void {
    const affair = this._context?.affair
    const proposal = this.selectedProposal
    if (!affair || !proposal?.technicianId || !this.canSubmit) return

    this.submitting = true
    this.submitError = null

    // Convention vivier : l'heure « métier » est encodée en UTC
    const startDate = `${proposal.date}T${this.startTime}:00.000Z`

    this.interventionsService
      .schedulePoolAffair(affair.affairId, {
        technicianId: proposal.technicianId,
        agencyId: this.selectedAgencyId!,
        startDate,
        isConfirmed: this.isConfirmed,
        isTimeSlot: this.isTimeSlot,
        startTimeSlot: this.isTimeSlot ? this.startTimeSlot : null,
        endTimeSlot: this.isTimeSlot ? this.endTimeSlot : null,
        customerFirstname: this.customerFirstname.trim() || null,
        customerPhone: this.customerPhone.trim() || null,
        customerEmail: this.customerEmail.trim() || null,
      })
      .pipe(finalize(() => (this.submitting = false)))
      .subscribe({
        next: (response) => {
          this.openAffairOnEros(response)
          this.scheduled.emit(response)
          this.close()
        },
        error: (err) => {
          const body = err?.error as SchedulePoolAffairResponse | undefined
          if (body?.status === 'affair_created_intervention_failed') {
            // L'affaire EROS existe : la reprise réutilisera le même mlRef
            this.partialFailure = body
            this.submitError = null
          } else {
            this.submitError =
              body?.message ??
              (err?.error?.message ||
                'La création dans EROS a échoué. Réessayez.')
          }
        },
      })
  }

  formatProposalTime(proposal: AffairOptimizationSlotData): string {
    return this.extractUtcTime(proposal.arrivalTime) ?? '—'
  }

  /**
   * Ouvre la fiche de l'affaire créée sur l'app EROS correspondante dans un
   * nouvel onglet (même logique que le wizard / la page schedule-eros).
   */
  private openAffairOnEros(response: SchedulePoolAffairResponse): void {
    if (!response.erosAffairId) return

    if (response.erpSource === 'abm') {
      window.open(
        environment.abmUrl + 'affairs/' + response.erosAffairId + '/show',
        '_blank'
      )
    } else if (response.erpSource === 'nrj') {
      window.open(
        environment.nrjUrl + 'affairs/' + response.erosAffairId + '/show',
        '_blank'
      )
    }
  }
}
